import { Link } from "react-router-dom";
import MediaImage from "@/components/MediaImage";
import { Counter, PlayerLine, SectionHead, mono, num } from "@/components/home/Primitives";

export function Stats({ stats, fact, brk, lang, t }) {
  const loc = lang === "fr" ? "fr-FR" : "en-GB";
  const fmt = (d) => new Date(d).toLocaleDateString(loc, { day: "numeric", month: "long" });
  return (
    <section data-testid="home-stats">
      <SectionHead title={t("home.stats_title")} testid="home-stats-title" />
      <Counter value={stats.players} label={t("home.stat_players")} testid="home-stat-players" />
      {brk?.no_matches ? (
        <div className="border-b border-[var(--wl-line)] py-1.5" data-testid="home-stat-break">
          <p className="text-[12px] leading-snug" data-testid="home-break-text">
            <span className="font-bold text-[var(--wl-text)]">{t("home.break_last_played", { date: fmt(brk.last_played) })}</span>
            <span className="text-[var(--wl-muted)]"> · {t("home.break_resume", { date: fmt(brk.resume_date) })}</span>
          </p>
        </div>
      ) : (
        <div className="border-b border-[var(--wl-line)] py-1.5" data-testid="home-stat-milestones">
          <div className="flex items-baseline justify-between gap-3">
            <p className={mono}>{t("home.stat_milestones")}</p>
            <p className={`${num} text-xl font-bold text-[var(--wl-text)]`} data-testid="home-stat-milestones-value">{stats.milestones_week}</p>
          </div>
          {stats.milestones_week === 0 && brk?.last_played && (
            <p className="mt-0.5 text-[12px] leading-snug text-[var(--wl-muted)]" data-testid="home-nocap">{t("home.no_cap_since", { date: fmt(brk.last_played) })}</p>
          )}
        </div>
      )}
      <Counter value={stats.leagues} label={t("home.stat_leagues")} testid="home-stat-leagues" />
      {fact && <p className="mt-1.5 text-[12px] text-[var(--wl-muted)]" data-testid="home-fact">{t(`home.fact_${fact.template}`, fact)}</p>}
    </section>
  );
}

export function Feed({ feed, nextFixture, brk, t, lang }) {
  const items = feed.items || [];
  const loc = lang === "fr" ? "fr-FR" : "en-GB";
  const fmt = (d) => new Date(d).toLocaleDateString(loc, { day: "numeric", month: "long" });
  const title = brk?.no_matches
    ? t("home.feed_title_break", { date: fmt(brk.last_played) })
    : feed.window_days > 7 ? t("home.feed_title_30") : t("home.feed_title");
  return (
    <section data-testid="home-feed" data-window={feed.window_days}>
      <SectionHead title={title} link="/week" linkLabel={t("home.see_all")} testid="home-feed-title" />
      {items.length === 0 ? (
        <p className="py-2 text-[12px] text-[var(--wl-muted)]" data-testid="home-feed-empty">
          {t("home.feed_empty")}{nextFixture ? ` ${t("home.next_match", { date: new Date(nextFixture.utc_date).toLocaleString(lang === "fr" ? "fr-FR" : "en-GB", { weekday: "long", hour: "2-digit", minute: "2-digit" }) })}` : ""}
        </p>
      ) : items.map((r) => <PlayerLine key={r.player_id} r={r} t={t} lang={lang} testid={`home-feed-row-${r.player_id}`} />)}
    </section>
  );
}

export function Sets({ sets, t }) {
  if (!sets.length) return null;
  return (
    <section data-testid="home-sets">
      <SectionHead title={t("home.sets_title")} link="/cards" linkLabel={t("home.see_all")} testid="home-sets-title" />
      {sets.map((s) => (
        <Link key={s.slug} to={`/cards/${s.slug}`} className="flex items-center justify-between gap-2 border-b border-[var(--wl-line)] py-1.5 transition-colors hover:bg-[var(--wl-panel)]" data-testid={`home-set-${s.slug}`}>
          <span className="min-w-0"><span className="block truncate text-[12.5px] font-bold text-[var(--wl-text)]">{s.set_name}</span><span className={`${mono} block normal-case tracking-normal`}>{s.manufacturer} · {s.season}</span></span>
          <span className={`${num} shrink-0 text-[var(--wl-muted)]`}>{t("home.cards_n", { n: s.cards })}</span>
        </Link>
      ))}
    </section>
  );
}

const row = "flex h-8 items-center gap-2 border-b border-[var(--wl-line)] transition-colors hover:bg-[var(--wl-panel)]";

export function Upcoming({ fixtures, nextFixture, brk, t, lang }) {
  const loc = lang === "fr" ? "fr-FR" : "en-GB";
  const countdown = brk?.no_matches
    ? (brk.resume_in_days <= 0 ? t("home.countdown_now") : brk.resume_in_days === 1 ? t("home.countdown_one") : t("home.countdown", { n: brk.resume_in_days }))
    : null;
  return (
    <section data-testid="home-upcoming">
      <SectionHead title={t("home.upcoming_title")} link="/matches" linkLabel={t("home.see_all")} testid="home-upcoming-title" />
      {countdown && <p className="py-1 text-[12px] font-bold text-[var(--wl-accent)]" data-testid="home-resume-countdown">{countdown}</p>}
      {fixtures.length === 0 ? (
        <p className="py-2 text-[12px] text-[var(--wl-muted)]" data-testid="home-upcoming-empty">
          {nextFixture ? t("home.next_matchday", { date: new Date(nextFixture.utc_date).toLocaleDateString(loc, { day: "numeric", month: "long" }) }) : t("home.no_fixture")}
        </p>
      ) : fixtures.map((f) => (
        <Link key={f.fixture_id} to="/matches" className={`${row} h-auto min-h-8 py-1`} data-testid={`home-fixture-${f.fixture_id}`}>
          <MediaImage src={f.home_logo} alt={t("media.logo_alt", { club: f.home_name })} kind="club" size={16} radius="rounded-sm" surface="watchlist" imgClassName="object-contain" />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[12.5px] text-[var(--wl-text)]">{f.home_name} – {f.away_name}</span>
            {f.tracked > 0 && <span className="block text-[12px] text-[var(--wl-muted)]" data-testid={`home-fixture-tracked-${f.fixture_id}`}>{t("home.tracked_n", { n: f.tracked })}</span>}
          </span>
          <span className={`${num} shrink-0 text-[11px] text-[var(--wl-muted)]`}>{new Date(f.utc_date).toLocaleString(loc, { weekday: "short", hour: "2-digit", minute: "2-digit" })}</span>
        </Link>
      ))}
    </section>
  );
}

export function Leagues({ leagues, t }) {
  return (
    <section data-testid="home-leagues">
      <SectionHead title={t("home.leagues_title")} testid="home-leagues-title" />
      {leagues.map((l) => (
        <Link key={l.id} to={`/watchlist?league=${encodeURIComponent(l.name)}`} className={row} data-testid={`home-league-${l.id}`}>
          <MediaImage src={l.logo} alt={t("media.logo_alt", { club: l.name })} kind="club" size={16} radius="rounded-sm" surface="watchlist" imgClassName="object-contain" />
          <span className="min-w-0 flex-1 truncate text-[12.5px] text-[var(--wl-text)]">{l.name}</span>
          <span className={`${num} shrink-0 text-[12px] text-[var(--wl-muted)]`} data-testid={`home-league-count-${l.id}`}>{l.players}</span>
        </Link>
      ))}
    </section>
  );
}
